import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_STANDING_DEFAULTS } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { StandingDefault } from '@/types/domain'

/* The blocks every new quote is seeded with (N-19).
 *
 * Settings → «Шаблоны КП» used to show empty «Включено», «Не включено» and
 * «Оговорки» cards while each new quote received six inclusions, nine
 * exclusions and four notes: the text lives in code (`quote.DefaultBlocks`),
 * not in the template library that screen edits, so an administrator could
 * neither see nor change what was actually used. This is the read that makes it
 * visible. It is deliberately a SEPARATE read from `useDocumentTemplates`: the
 * library is a list of rows somebody maintains, these are fixed wording, and
 * folding the two into one list would offer edit and delete on rows the server
 * would refuse.
 *
 * Open to every session (the wording prints on every offer), ordered by the
 * server: kind in print order, then language (ru first), then position. */
export function useStandingDefaults() {
  return useQuery<StandingDefault[]>({
    queryKey: queryKeys.settings.standingDefaults(),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_STANDING_DEFAULTS
        : (
            await api.get<{ items: StandingDefault[] }>(
              '/settings/templates/standing-defaults',
            )
          ).items,
  })
}

/** What the panel shows for one kind: the reader's language, or Russian when the
 *  server sent none in it — a language added to the product before its wording
 *  is translated must still show a standing text rather than an empty card.
 *  Position order is the server's and is kept. */
export function standingDefaultsFor(
  items: readonly StandingDefault[],
  kind: StandingDefault['kind'],
  language: string,
): StandingDefault[] {
  const ofKind = items.filter((item) => item.kind === kind)
  const own = ofKind.filter((item) => item.language === language)
  return own.length > 0 ? own : ofKind.filter((item) => item.language === 'ru')
}
